import React, { Suspense, useState, useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthProvider';
import { SubscriptionProvider } from '../../contexts/SubscriptionContext';
import { useTimezone } from '../../hooks/useTimezone';
import { useIsAdmin } from '../../utils/adminUtils';
import { AppLayout } from '../AppLayout';
import { Link } from 'react-router-dom';
import { BetaCodeModal } from '../auth/BetaCodeModal';
import apiClient from '../../services/apiClient';
import { logger } from '../../utils/logger';

const RouteLoader: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-400 mx-auto mb-4"></div>
      <p className="text-gray-600">Loading...</p>
    </div>
  </div>
);

interface RouteProps {
  children: React.ReactNode;
}

export const ProtectedRoute: React.FC<RouteProps> = ({ children }) => {
  const { loading, session, user } = useAuth();
  // Beta mode disabled - open access for all users
  const betaDisabled = true;
  // Initialize showBetaModal to true to prevent premature redirect
  // Will be set to false in useEffect if user has a stored beta code OR beta is disabled
  const [showBetaModal, setShowBetaModal] = useState(!betaDisabled);
  const [betaCodeError, setBetaCodeError] = useState<string | null>(null);
  const [isValidatingBetaCode, setIsValidatingBetaCode] = useState(false);
  const [hasDismissedBetaModal, setHasDismissedBetaModal] = useState(false);
  const navigate = useNavigate();

  useTimezone();

  // Check if user has valid beta access stored in localStorage
  useEffect(() => {
    // Skip beta check entirely if beta is disabled
    if (betaDisabled) {
      setShowBetaModal(false);
      return;
    }

    if (!loading && !session) {
      const storedBetaCode = localStorage.getItem('vitly_beta_code');
      if (storedBetaCode) {
        // User has a stored beta code, hide modal and validate it
        setShowBetaModal(false);
        validateStoredBetaCode(storedBetaCode);
      }
      // If no stored code, modal stays visible (already initialized to true)
    } else if (session) {
      // User is authenticated, hide the modal
      setShowBetaModal(false);
    }
  }, [loading, session, betaDisabled]);

  const validateStoredBetaCode = async (code: string) => {
    try {
      const response = await apiClient.api.post('/v1/beta/validate-code', { code });
      if (response.data && response.data.valid) {
        logger.debug('[ProtectedRoute] Stored beta code is valid');
        // Keep the user on the page, they have valid beta access
        return;
      }
    } catch (_error) {
      logger.warn('[ProtectedRoute] Stored beta code is invalid, clearing');
      localStorage.removeItem('vitly_beta_code');
      setShowBetaModal(true);
    }
  };

  const handleBetaCodeSubmit = async (code: string) => {
    setIsValidatingBetaCode(true);
    setBetaCodeError(null);

    try {
      // Validate the beta code with the backend
      const response = await apiClient.api.post('/v1/beta/validate-code', { code });

      if (response.data && response.data.valid) {
        logger.debug('[ProtectedRoute] Beta code validated successfully');

        // Store the valid beta code
        localStorage.setItem('vitly_beta_code', code);

        // Close modal and redirect to register with the beta code
        setShowBetaModal(false);
        navigate('/register', { state: { betaCode: code } });
      } else {
        setBetaCodeError('This beta code is not active');
      }
    } catch (error: any) {
      logger.error('[ProtectedRoute] Beta code validation error:', error);

      if (error?.response?.status === 404) {
        setBetaCodeError('Invalid beta code. Please check and try again.');
      } else if (error?.response?.status === 403) {
        setBetaCodeError('This beta code has reached its usage limit.');
      } else {
        setBetaCodeError('Failed to validate beta code. Please try again.');
      }
    } finally {
      setIsValidatingBetaCode(false);
    }
  };

  const handleBetaModalClose = () => {
    setShowBetaModal(false);
    setHasDismissedBetaModal(true);
  };

  if (loading) {
    return <RouteLoader />;
  }

  const isAuthenticated = !!session && !!user;

  // If user is authenticated, allow access
  if (isAuthenticated) {
    return (
      <SubscriptionProvider>
        <Suspense fallback={<RouteLoader />}>
          {children}
        </Suspense>
      </SubscriptionProvider>
    );
  }

  // User is not authenticated
  // Show beta modal if they haven't dismissed it and don't have a valid beta code
  if (showBetaModal && !hasDismissedBetaModal) {
    return (
      <BetaCodeModal
        isOpen={showBetaModal}
        onClose={handleBetaModalClose}
        onSubmit={handleBetaCodeSubmit}
        isLoading={isValidatingBetaCode}
        error={betaCodeError}
      />
    );
  }

  // If modal was dismissed or they have valid beta code, redirect to login
  return <Navigate to="/login" replace />;
};

export const AdminRoute: React.FC<RouteProps> = ({ children }) => {
  const { loading, session, user } = useAuth();
  const isAdminUser = useIsAdmin();

  useTimezone();

  if (loading) {
    return <RouteLoader />;
  }

  if (!session || !user) {
    return <Navigate to="/login" replace />;
  }

  if (!isAdminUser) {
    return (
      <SubscriptionProvider>
        <AppLayout>
          <div className="p-10 max-w-3xl mx-auto">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-yellow-800">
              <h2 className="text-xl font-semibold mb-2">Admin Access Required</h2>
              <p className="text-sm">
                You’re signed in, but your account doesn’t have permission to view the admin console.
                If you believe this is a mistake, please contact a workspace administrator.
              </p>
              <Link to="/dashboard" className="inline-block mt-4 text-sm text-blue-600 hover:text-blue-700">
                Return to dashboard →
              </Link>
            </div>
          </div>
        </AppLayout>
      </SubscriptionProvider>
    );
  }

  return (
    <SubscriptionProvider>
      <Suspense fallback={<RouteLoader />}>
        {children}
      </Suspense>
    </SubscriptionProvider>
  );
};

export default ProtectedRoute;
